import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewEncapsulation,
    forwardRef,
    signal,
} from "@angular/core";
import { provideTranslocoScope, TranslocoPipe } from "@jsverse/transloco";
import { CerrixLabelComponent } from "@app/shared/atoms/cerrix-label/cerrix-label.component";
import { CerrixTabsComponent } from "@app/shared/molecules/cerrix-tabs/cerrix-tabs.component";
import { CerrixTabDirective } from "@app/shared/molecules/cerrix-tabs/cerrix-tab.directive";
import { CerrixSearchInputComponent } from "@app/shared/atoms/cerrix-search-input/cerrix-search-input.component";
import {
    CerrixSelectListComponent,
    CerrixSelectListOption,
} from "@app/shared/molecules/cerrix-select-list/cerrix-select-list.component";
import { IdGuidNameCombination } from "@models/generic/IdGuidNameCombination";
import { CerrixInstructionsIconComponent } from "@app/shared/cerrix-instructions-icon/cerrix-instructions-icon.component";
import { CerrixInputValidationErrorsDirective } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-errors.directive";
import {
    CERRIX_INPUT_VALUE_SOURCE,
    CerrixInputValueSource,
} from "@app/shared/directives/cerrix-input-fields/models/cerrix-input-value-source.interface";

/**
 * A field that lets the user pick a single user for any role (owner, responsible, informed, ...)
 * from a caller-supplied user list. The caller supplies the users and translates `label` (it
 * names the role being picked); everything else (search, tab labels) is translated by this
 * component. Set `multiselect` and bind `values`/`valuesChange` instead of `value`/`valueChange`
 * to pick more than one user.
 */
@Component({
    selector: "app-cerrix-user-picker",
    templateUrl: "./cerrix-user-picker.component.html",
    styleUrls: ["./cerrix-user-picker.component.scss"],
    imports: [
        TranslocoPipe,
        CerrixLabelComponent,
        CerrixTabsComponent,
        CerrixTabDirective,
        CerrixSearchInputComponent,
        CerrixSelectListComponent,
        CerrixInstructionsIconComponent,
        CerrixInputValidationErrorsDirective,
    ],
    providers: [
        provideTranslocoScope({
            scope: "shared/organisms/cerrix-user-picker",
            alias: "cerrix-user-picker",
        }),
        {
            provide: CERRIX_INPUT_VALUE_SOURCE,
            useExisting: forwardRef(() => CerrixUserPickerComponent),
        },
    ],
    encapsulation: ViewEncapsulation.None,
})
export class CerrixUserPickerComponent implements OnChanges, CerrixInputValueSource<string[]> {
    @Input() name = "user-picker";
    @Input() users: IdGuidNameCombination[] = [];
    @Input() value: string;
    @Input() readonly: boolean = false;

    @Output() valueChange: EventEmitter<string> = new EventEmitter<string>();

    // Additive multiselect mode: leaves `value`/`valueChange` untouched for existing single-select
    // callers, and only reads/writes `values`/`valuesChange` while `multiselect` is on.
    @Input() multiselect: boolean = false;
    @Input() values: string[] = [];
    @Output() valuesChange: EventEmitter<string[]> = new EventEmitter<string[]>();

    @Input() label: string;
    @Input() infoText: string = "";

    protected selectedValues: string[] = [];
    protected filter: string = "";

    // The picked users live in this component, so the required validator watches them here
    // rather than reading a DOM input. They are kept as a list in both modes.
    private pickedValue = signal<string[]>([]);
    readonly validationValue = this.pickedValue.asReadonly();

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["value"] || changes["values"] || changes["multiselect"]) {
            this.selectedValues = this.multiselect
                ? (this.values ?? [])
                : this.value
                  ? [this.value]
                  : [];
            this.pickedValue.set(this.selectedValues);
        }

        // A new `users` list means this instance now represents a different picker (e.g. a
        // switched type tab reusing this component), so a stale search term must not carry over.
        if (changes["users"]) {
            this.filter = "";
        }
    }

    public filteredUserList(): CerrixSelectListOption[] {
        return this.userOptions().filter((user) =>
            user.label.toLowerCase().includes(this.filter.toLowerCase()),
        );
    }

    public selectedUserList(): CerrixSelectListOption[] {
        return this.userOptions().filter((user) => this.selectedValues.includes(user.value));
    }

    public setSelectedUsers(values: string[]): void {
        this.selectedValues = values;
        this.pickedValue.set(values);
        if (this.multiselect) {
            this.values = values;
            this.valuesChange.emit(this.values);
        } else {
            this.value = values[0] ?? null;
            this.valueChange.emit(this.value);
        }
    }

    private userOptions(): CerrixSelectListOption[] {
        return this.users.map((user) => ({
            label: user.Name,
            value: user.Guid,
            avatarName: user.Name,
        }));
    }
}
